import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ConfiguratorContext, CONFIGURATOR_CONTEXT } from '../configurator-context';
import { InstanceAction, withInstanceScope } from './instance-actions';

/**
 * Base class for instance-aware NgRx facades.
 *
 * This class provides automatic instance scoping for actions and selectors,
 * ensuring that each configurator instance operates on its own isolated state.
 *
 * When a `CONFIGURATOR_CONTEXT` is injected, the facade automatically:
 * - Scopes all dispatched actions to the current instance
 * - Filters selectors to only emit values for the current instance
 * - Cleans up subscriptions when the instance is destroyed
 *
 * When no context is provided (legacy mode), the facade operates
 * on the root state as before.
 *
 * @example
 * ```typescript
 * @Injectable()
 * export class ExternalVenetianFacade extends InstanceAwareFacade {
 *     constructor(
 *         store: Store,
 *         @Optional() @Inject(CONFIGURATOR_CONTEXT) context: ConfiguratorContext | null
 *     ) {
 *         super(store, context);
 *     }
 *
 *     // Selectors automatically use instance scope
 *     configuration$ = this.select(externalVenetianQuery.getConfiguration);
 *
 *     // Actions automatically dispatched with instance scope
 *     setShutterType(shutterType: ShutterType) {
 *         this.dispatch(new SetExternalVenetianShutterType({ shutterType }));
 *     }
 * }
 * ```
 */
@Injectable()
export abstract class InstanceAwareFacade implements OnDestroy {
    /**
     * Subject that emits when the facade is destroyed.
     * Used for automatic cleanup of subscriptions.
     */
    protected readonly destroy$ = new Subject<void>();

    /**
     * The instance ID from the context, or 'legacy' for backward compatibility
     */
    protected readonly instanceId: string;

    /**
     * Whether this facade is operating in instance-scoped mode
     */
    protected readonly isInstanceScoped: boolean;

    constructor(
        protected readonly store: Store,
        @Optional() @Inject(CONFIGURATOR_CONTEXT) protected readonly context: ConfiguratorContext | null
    ) {
        this.instanceId = context?.instanceId ?? 'legacy';
        this.isInstanceScoped = !!context;

        // Subscribe to context destruction for cleanup
        if (context) {
            context.destroy$.pipe(
                takeUntil(this.destroy$)
            ).subscribe(() => {
                this.onContextDestroy();
            });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Hook called when the configurator context is destroyed.
     * Override to perform custom cleanup.
     */
    protected onContextDestroy(): void {
        // Default: trigger destroy$ to clean up subscriptions
        this.destroy$.next();
    }

    /**
     * Dispatch an action with automatic instance scoping.
     *
     * When in instance-scoped mode, the action is automatically
     * wrapped with the instance ID metadata.
     */
    protected dispatch(action: Action): void {
        if (this.isInstanceScoped && this.context) {
            this.store.dispatch(withInstanceScope(action, this.context.instanceId) as any);
        } else {
            this.store.dispatch(action as any);
        }
    }

    /**
     * Select from the store with automatic takeUntil(destroy$).
     *
     * This ensures subscriptions are automatically cleaned up
     * when the facade is destroyed.
     */
    protected select<T>(selector: (state: any) => T): Observable<T> {
        return this.store.select(selector).pipe(
            takeUntil(this.destroy$)
        );
    }

    /**
     * Select from the store with instance filtering.
     *
     * When in instance-scoped mode, this will filter emissions
     * to only include values for the current instance.
     *
     * @param selector The selector function
     * @param instanceKeyExtractor Optional function to extract instance ID from selected value
     */
    protected selectForInstance<T>(
        selector: (state: any) => T,
        instanceKeyExtractor?: (value: T) => string | undefined
    ): Observable<T> {
        return this.store.select(selector).pipe(
            filter((value) => {
                if (!this.isInstanceScoped) {
                    return true; // Legacy mode - no filtering
                }
                if (!instanceKeyExtractor) {
                    return true; // No extractor - trust the selector
                }
                const valueInstanceId = instanceKeyExtractor(value);
                return valueInstanceId === this.instanceId || valueInstanceId === undefined;
            }),
            takeUntil(this.destroy$)
        );
    }

    /**
     * Get the current instance ID.
     * Returns 'legacy' if not in instance-scoped mode.
     */
    getInstanceId(): string {
        return this.instanceId;
    }

    /**
     * Check if the facade is in instance-scoped mode.
     */
    isScoped(): boolean {
        return this.isInstanceScoped;
    }
}

import { InstanceAwareState } from './instance-selectors';

/**
 * Create a selector that reads from the current instance's state.
 *
 * @example
 * ```typescript
 * const getConfiguration = createInstanceSelector(
 *     selectExternalVenetianState,
 *     (state) => state.configuration
 * );
 * ```
 */
export function createInstanceSelectorWithContext<TState, TResult>(
    baseSelector: (state: any) => InstanceAwareState<TState> | undefined,
    projector: (instanceState: TState | undefined) => TResult
): (state: any, instanceId: string) => TResult {
    return (state: any, instanceId: string) => {
        const featureState = baseSelector(state);
        const instanceState = featureState?.instances?.[instanceId];
        return projector(instanceState);
    };
}
